'use client';

import { useCallback, useEffect, useState } from 'react';

import classnames from 'classnames';

import type { PaginationProps } from './Pagination.types';

import styles from './Pagination.module.scss';

const LEFT_PAGE = -1;
const RIGHT_PAGE = -2;

const range = (from: number, to: number, step = 1) => {
  let i = from;
  const range = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
};

function Pagination({
  style,
  className,
  pageNumber,
  pageSize,
  totalRecords,
  pageNeighbours = 2,
  onPageChange,
  testId = 'ui-pagination',
}: PaginationProps) {
  const [totalPages, setTotalPages] = useState(
    Math.ceil(totalRecords / pageSize),
  );
  const calculateCurrentPage = useCallback(
    (page: number) => Math.max(1, Math.min(page, totalPages)),
    [totalPages],
  );

  const [currentPage, setCurrentPage] = useState(
    calculateCurrentPage(pageNumber),
  );
  const [pages, setPages] = useState<number[]>([]);

  const fetchPageNumbers = useCallback(() => {
    const totalNumbers = pageNeighbours * 2 + 1;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);
      let pages = range(startPage, endPage);

      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, RIGHT_PAGE];
          break;
        }
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  }, [currentPage, totalPages, pageNeighbours]);

  useEffect(() => {
    setPages(fetchPageNumbers());
  }, [currentPage, fetchPageNumbers]);

  useEffect(() => {
    const currentPage = calculateCurrentPage(pageNumber);
    setCurrentPage(currentPage);
  }, [calculateCurrentPage, pageNumber]);

  useEffect(() => {
    setTotalPages(Math.ceil(totalRecords / pageSize));
  }, [pageSize, totalRecords]);

  const goToPage = (page: number) => {
    const currentPage = calculateCurrentPage(page);
    setCurrentPage(currentPage);
    onPageChange(currentPage);
  };

  return (
    <div
      style={style}
      className={classnames(styles['pagination'], className)}
      data-testid={testId}
    >
      <div className={classnames(styles['container'])}>
        {pages.map((page, index) => {
          if (page === LEFT_PAGE)
            return (
              <div
                className={classnames(
                  styles['page'],
                  styles['page__separator'],
                )}
                key={index}
                onClick={() => goToPage(currentPage - 1)}
                data-testid={`${testId}_page-${index}`}
              >
                <span>&laquo;</span>
              </div>
            );

          if (page === RIGHT_PAGE)
            return (
              <div
                className={classnames(
                  styles['page'],
                  styles['page__separator'],
                )}
                key={index}
                onClick={() => goToPage(currentPage + 1)}
                data-testid={`${testId}_page-${index}`}
              >
                <span>&raquo;</span>
              </div>
            );

          return (
            <div
              className={classnames(
                styles['page'],
                currentPage === page && styles['page--active'],
              )}
              key={index}
              onClick={() => goToPage(page)}
              data-testid={`${testId}_page-${index}`}
            >
              {page}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { Pagination };
